import { push } from '@lagunovsky/redux-react-router'
import { compact, groupBy, isNil, keys, mapValues, omit, orderBy, take, uniq, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { H_PIEZO_DASHBOARD } from '../../../account/constants/AccessRulesConstants'
import AdministrationAction from '../../../administration/actions/AdministrationAction'
import CampaignPortletPanel from '../../../campaign/components/CampaignPortletPanel'
import { getCampaignIcon, getCampaignNameCell, getCampaignProgress, getCampaignsWithBookmarks, getProgressBar } from '../../../campaign/utils/CampaignUtils'
import useCampaignBookmarks from '../../../utils/customHook/useCampaignBookmarks'
import ProgressCard from '../../../components/card/ProgressCard'
import CartographyPanel from '../../../components/map/CartographyPanel'
import PiezometryAction from '../../../piezometry/actions/PiezometryAction'
import { FR, nbPerPageLabel, SANDRE } from '../../../referencial/constants/ReferencialConstants'
import StationAction from '../../../station/actions/StationAction'
import { STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import { getColorCampaign } from '../../../utils/CampaignUtil'
import { getDate, getTimestamp } from '../../../utils/DateUtil'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { hasValue } from '../../../utils/NumberUtil'
import { getBookmarks, getStationDesignation, hasLocalisationStation } from '../../../utils/StationUtils'
import {
    getLabel,
} from '../../../utils/StoreUtils'
import { getDeptCode, getI18nTitleData, normalizeString } from '../../../utils/StringUtil'
import { getUserBookmarksByStationType } from '../../../utils/UserUtil'
import DtoPiezometerListSpecific from '../../dto/DtoPiezometerListSpecific'
import AddPiezoStepper from './addPiezo/AddPiezoStepper'
import { CardTitle } from '../../../components/card/NewCard'
import { Accordion, AccordionDetails, AccordionSummary, Card, CardContent, CircularProgress, Grid, Icon, IconButton } from '@mui/material'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import useTitle from 'utils/customHook/useTitle'
import useBoolean from 'utils/customHook/useBoolean'
import SimpleTabList from 'components/list/SimpleTabList'
import useSandreList from 'utils/customHook/useSandreList'
import ReferencialAction from 'referencial/action/ReferencialAction'
import UserAction from 'administration/components/user/actions/UserAction'
import CampaignAction from 'campaign/actions/CampaignAction'
import NetworkAction from 'referencial/components/network/actions/NetworkAction'
import CityAction from 'referencial/components/city/actions/CityAction'
import useActions from 'utils/customHook/useActions'
import StationFilterFields from 'station/components/search/StationFilterFields'
import { ALIGN, CardTable, sortRows } from 'components/datatable/NewTable'
import Select from 'components/forms/Select'
import { convertCoordinates, PROJECTIONS } from 'utils/mapUtils/CoordinateUtils'
import ExportFileModal from 'components/modal/ExportFileModal'
import ExportAction from 'export/actions/ExportAction'
import { formatData } from 'utils/ExportDataUtil'
import ToastrAction from 'toastr/actions/ToastrAction'
import useAbortController from 'utils/customHook/useAbortController'
import SimpleKeyFigurePanel from 'components/keyFigures/SimpleKeyFigurePanel'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { DEFAULT_CONTRIBUTOR_TYPE } from 'administration/components/user/constants/UserConstants'
import moment from 'moment'

const CARTO_STATION = 'cartoStation'
const LIST_STATION = 'listStation'
const REFERENT = 'referent'

const ExportModal = ({
    isOpen,
    onClose = () => {},
    filteredPiezometers = [],
}) => {
    const dispatch = useDispatch()
    const {
        departments,
        piezometersReferents,
        piezometersOperators,
    } = useSelector(store => ({
        departments: store.CityReducer.departments,
        piezometersReferents: store.PiezometryReducer.piezometersReferents,
        piezometersOperators: store.PiezometryReducer.piezometersOperators,
    }), shallowEqual)
    const citiesIndex = useSelector(store => store.CityReducer.citiesIndex, shallowEqual)

    const [projection, setProjection] = useState()

    const getLocalisation = useCallback(piezo => {
        if (hasLocalisationStation(piezo)) return { x: piezo.x, y: piezo.y, projection: piezo.projection }

        const city = citiesIndex[piezo.townCode]
        if (city && (!city.countryCode || city.countryCode === FR) && hasLocalisationStation(city)) {
            return { x: city.x, y: city.y, projection: city.projection }
        }

        return {}
    }, [citiesIndex])

    const piezoNatures = useSandreList(SANDRE.PIEZOMETER_NATURE)

    const onExport = (type) => {
        const headers = ['code', 'internalReferences', 'department', 'cityCode', 'cityLabel', 'name', 'creationDate', 'closeDate', 'piezometerNature', 'administrator', 'x', 'y', 'projection']
        const piezometersFormated = filteredPiezometers.map(piezo => {
            const referentLink = piezometersReferents.find(link => link.idStation === piezo.id) || {}
            const operatorLink = piezometersOperators.find(link => link.idStation === piezo.id) || {}
            const city = citiesIndex[piezo.townCode]

            const creationDate = getDate(piezo.creationDate)
            const loc = getLocalisation(piezo)
            const usedLoc = !isNil(projection) ? convertCoordinates(loc.projection, projection, loc) : loc


            const deptCode = piezo.townCode && city && (!city.countryCode || city.countryCode === 'FR')
                ? getDeptCode(piezo.townCode)
                : ''

            const department = deptCode
                ? `${getLabel(departments, deptCode)} [${deptCode}]`
                : ''

            return {
                id: piezo.id,
                code: { value: getStationDesignation(piezo), cellType: 'string' },
                internalReferences: { ...piezo.internalReferences, cellType: 'string' },
                department,
                cityCode: { value: piezo.townCode, cellType: 'string' },
                cityLabel: city?.name,
                name: piezo.name || '',
                creationDate,
                closeDate: getDate(piezo.closeDate),
                piezometerNature: getLabel(piezoNatures, piezo.nature),
                administrator: referentLink.name,
                referentId: referentLink.idContributor,
                operatorId: operatorLink.idContributor,
                searchValue: normalizeString(`${piezo.code}${piezo.internalReferences}${piezo.name}${city?.name || ''}${piezo.townCode}${creationDate}${referentLink.name}${operatorLink.name}${department}`),
                x: { value: usedLoc.x, format: '0.0000000000', cellType: 'number' },
                y: { value: usedLoc.y, format: '0.0000000000', cellType: 'number' },
                projection: { value: projection ?? usedLoc.projection, format: '0', cellType: 'number' },
            }
        })

        if (piezometersFormated.length) {
            const exportData = [
                {
                    ...piezometersFormated[0],
                    headers,
                },
                ...piezometersFormated.slice(1),
            ]
            dispatch(ExportAction.export(formatData(exportData), type, i18n.watchpoints))
        } else {
            dispatch(ToastrAction.warning(i18n.noDataToExport))
        }
    }

    return (
        <ExportFileModal
            open={isOpen}
            onClose={onClose}
            data={[
                {
                    name: i18n.resultsTable,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExport('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExport('csv'),
                    }],
                    settingsContent: (
                        <Select
                            label={i18n.coordinateConversion}
                            value={projection}
                            onChange={setProjection}
                            options={PROJECTIONS}
                            keyLabel='name'
                            keyValue='sandreCode'
                        />
                    ),
                },
            ]}
        />
    )
}

ExportModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    filteredPiezometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const CampaignPanel = () => {
    const {
        piezometryCampaigns,
        piezometryCampaignsProgress,
        contacts,
    } = useSelector(store => ({
        piezometryCampaigns: store.PiezometryReducer.piezometryCampaigns,
        piezometryCampaignsProgress: store.PiezometryReducer.piezometryCampaignsProgress,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const dispatch = useDispatch()

    const { bookmarkIds } = useCampaignBookmarks(STATION_TYPE_NAME.piezometry)

    const orderedCampaigns = useMemo(() => {
        const currentCampaigns = orderBy(piezometryCampaigns.filter(c => c.statut === 2), c => getTimestamp(c.beginningApplication), 'desc')
        return getCampaignsWithBookmarks(piezometryCampaigns, currentCampaigns, bookmarkIds)
    }, [piezometryCampaigns, bookmarkIds])

    const {
        controllerRef,
        initController,
    } = useAbortController()

    useEffect(() => {
        initController()
        if (orderedCampaigns.length) {
            dispatch(CampaignAction.getCampaignProgressSequentially(STATION_TYPE_NAME.piezometry, take(orderedCampaigns, 3), controllerRef.current.signal))
        }
    }, [orderedCampaigns, controllerRef])

    const campaignsFormated = useMemo(() => orderedCampaigns.map(campaign => {
        const {
            nbStation = 0,
            nbStationValidated = 0,
            id,
        } = piezometryCampaignsProgress.find(progress => progress.id === campaign.id) || {}
        const progress = getCampaignProgress(nbStationValidated, nbStation)
        return {
            id: campaign.id,
            qualificationTableColor: {
                color: getColorCampaign(false, progress, 2),
            },
            nullValue: getCampaignIcon(campaign.campaignType, 'small'),
            name: getCampaignNameCell(campaign.name, bookmarkIds.includes(campaign.id)),
            nbStations: nbStation,
            startDate: campaign.beginningApplication,
            endDate: campaign.endingApplication,
            progression: hasValue(id) && getProgressBar(nbStationValidated, nbStation, progress) || <CircularProgress size={25} />,
            referent: getLabel(contacts, campaign.contactCode, 'mnemonique'),
        }
    }), [orderedCampaigns, piezometryCampaignsProgress, contacts, bookmarkIds])

    const headerIcon = (
        <IconButton
            sx={{
                color: '#000',
                fontSize: '1.8rem',
                padding: '1px',
            }}
            onClick={() => dispatch(push('/piezometry/campaign'))}
        >
            <Icon fontSize='inherit'>nature_people</Icon>
        </IconButton>
    )

    return (
        <CampaignPortletPanel
            campaigns={campaignsFormated}
            stationType={STATION_TYPE_NAME.piezometry}
            headers={[
                { key: 'qualificationTableColor', value: headerIcon, width: 26, sticky: ALIGN.LEFT },
                { key: 'campaignIcon', value: '' }, { key: 'name', value: i18n.campaign },
                'nbStations', 'startDate', 'endDate', 'progression',
            ]}
        />
    )
}

const LegendPanel = () => (
    <Grid container spacing={1} style={{ padding: '4px' }} alignItems='center'>
        <Grid size='auto'>
            <Icon>wifi</Icon>
        </Grid>
        <Grid size='auto'>
            {i18n.automaticUpdate}
        </Grid>
    </Grid>
)

const TablePanel = ({
    piezometers = [],

    initialSort,
    onTableSort,
    onSaveSearchValue = () => { },
}) => {
    const dispatch = useDispatch()

    const {
        filterResults,
    } = useSelector(store => ({
        filterResults: store.StationReducer.filterResults,
    }), shallowEqual)

    const filterHeaders = useMemo(() => filterResults?.length ? keys(filterResults[0]) : [], [filterResults])

    useEffect(() => {
        onSaveSearchValue(piezometers)
    }, [piezometers])

    return (
        <CardTable
            title={i18n.watchpoints}
            actions={[{
                icon: 'info_outlined',
                tooltip: <LegendPanel />,
            }]}
            rows={piezometers}
            headers={[{ key: 'bookmark', value: '' }, { key: 'jobExeId', value: '' },
                'labelCode', { key: 'internalReferences', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.internalReferencesHeader}</span>) },
                'department', { key: 'cityCode', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.cityCodeHeader}</span>) },
                'cityLabel', 'name', 'creationDate', 'closeDate', 'piezometerNature', 'administrator', { key: 'campaigns', value: '' }, ...filterHeaders]}

            rowsPerPageOptions={nbPerPageLabel}
            onClickRow={({ id }) => dispatch(push(`/station/piezometry/${id}`))}
            defaultSort={initialSort}
            onSort={(sort) => onTableSort?.(piezometers, sort)}
        />
    )
}

TablePanel.propTypes = {
    piezometers: PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerListSpecific)),
    filter: PropTypes.object,
    initialSort: PropTypes.shape({
        column: PropTypes.string,
        sort: PropTypes.string,
    }),
    onTableSort: PropTypes.func,
    onSaveSearchValue: PropTypes.func,
}

const CartoPanel = ({
    piezometersFiltered = [],
}) => {
    const hasLocationPiezometers = useMemo(() => {
        return piezometersFiltered.filter(s => s && (hasLocalisationStation(s) || s.townCode))
    }, [piezometersFiltered])

    return (
        <Card>
            <CardTitle
                title={(
                    <>
                        <span style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{i18n.watchpoints}</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: 'normal' }}>&nbsp;{`(${hasLocationPiezometers.length} ${getI18nTitleData(i18n.element, i18n.elements, hasLocationPiezometers)})`}</span>
                    </>
                )}
            />
            <CardContent>
                <CartographyPanel
                    layers={['STATIONS_POINTS']}
                    componentType={STATION_TYPE_NAME.piezometry}
                    stationsPoints={piezometersFiltered}
                    stationsPanelTitle={i18n.stations}
                    heightToSubstract={450}
                    noStationPanel={false}
                />
            </CardContent>
        </Card>
    )
}

CartoPanel.propTypes = {
    piezometersFiltered: PropTypes.array,
}

const ReferentPanel = ({
    piezometers = [],
}) => {
    const {
        piezometersReferents,
        contributorsIndex,
    } = useSelector(store => ({
        piezometersReferents: store.PiezometryReducer.piezometersReferents,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
    }), shallowEqual)

    const {
        piezoWithoutReferent,
        ...piezoGroupByReferent
    } = groupBy(piezometers, p => {
        const referentLink = piezometersReferents.find(link => link.idStation === p.id)
        return hasValue(referentLink?.code) ? referentLink?.idContributor : 'piezoWithoutReferent'
    })

    return (
        <div style={{ padding: '10 10 0 10' }}>
            {keys(piezoGroupByReferent).map((idContributor, i) => {
                const {
                    [idContributor]: piezoFiltered = [],
                } = piezoGroupByReferent
                return (
                    <Accordion key={i} sx={{ marginBottom: 1 }} defaultExpanded={i === 0}>
                        <AccordionSummary>
                            <span style={{ fontSize: '1.5rem' }}>
                                {idContributor !== 'undefined' ? (contributorsIndex[idContributor] || {}).name : i18n.unknown}
                            </span>
                            <span style={{ fontSize: '1.5rem', fontWeight: 'normal' }}>
                                &nbsp;{`(${piezoFiltered.length} ${getI18nTitleData(i18n.element, i18n.elements, piezoFiltered)})`}
                            </span>
                        </AccordionSummary>
                        <AccordionDetails nopadding>
                            <TablePanel piezometers={piezoFiltered} />
                        </AccordionDetails>
                    </Accordion>
                )
            })}
            {piezoWithoutReferent && (
                <Accordion sx={{ marginBottom: 1 }} defaultExpanded={!keys(piezoGroupByReferent).length}>
                    <AccordionSummary>
                        <span style={{ fontSize: '1.5rem' }}>{i18n.unknown}</span>
                        <span style={{ fontSize: '1.5rem', fontWeight: 'normal' }}>&nbsp;{`(${piezoWithoutReferent.length} ${getI18nTitleData(i18n.element, i18n.elements, piezoWithoutReferent)})`}</span>
                    </AccordionSummary>
                    <AccordionDetails nopadding>
                        <TablePanel piezometers={piezoWithoutReferent} />
                    </AccordionDetails>
                </Accordion>
            )}
        </div>
    )
}

ReferentPanel.propTypes = {
    piezometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const PiezometersDashboardApp = () => {
    const dispatch = useDispatch()

    const {
        piezosIndex,
        piezometerListSpecific,
        piezometryKeyfigures,
        selectedSearchValues,
        accountUser,
        globalResearch,
        citiesIndex,
        departments,
        piezometersReferents,
        piezometersOperators,
        networkPiezoLink,
        userBookmarks,
        piezometryCampaigns,
        piezometryCampaignsProgress,
        piezometryCampaignStations,
        filterResults: filterResultsProps,
        networks,
        accountHabilitations,
    } = useSelector(store => ({
        piezosIndex: store.PiezometryReducer.piezosIndex,
        piezometerListSpecific: store.PiezometryReducer.piezometerListSpecific,
        piezometryKeyfigures: store.PiezometryReducer.piezometryKeyfigures,
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        accountUser: store.AccountReducer.accountUser,
        globalResearch: store.HomeReducer.globalResearch,
        citiesIndex: store.CityReducer.citiesIndex,
        departments: store.CityReducer.departments,
        piezometersReferents: store.PiezometryReducer.piezometersReferents,
        piezometersOperators: store.PiezometryReducer.piezometersOperators,
        networkPiezoLink: store.PiezometryReducer.networkPiezoLink,
        userBookmarks: store.UserReducer.userBookmarks,
        piezometryCampaigns: store.PiezometryReducer.piezometryCampaigns,
        piezometryCampaignsProgress: store.PiezometryReducer.piezometryCampaignsProgress,
        piezometryCampaignStations: store.PiezometryReducer.piezometryCampaignStations,
        filterResults: store.StationReducer.filterResults,
        networks: store.NetworkReducer.networks,
        accountHabilitations: store.AccountReducer.accountHabilitations,
    }), shallowEqual)

    useEffect(() => {
        if (!componentHasHabilitations(H_PIEZO_DASHBOARD)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
        }
    }, [])

    const {
        isLoaded,
        progress,
    } = useProgressDispatch(() => [
        dispatch(PiezometryAction.fetchPiezometersListSpecific()),
        dispatch(UserAction.fetchBookmarks()),
        dispatch(ReferencialAction.fetchSandreCodes()),
        dispatch(CityAction.fetchDepartments()),
        dispatch(PiezometryAction.fetchNetworkPiezoLink()),
        dispatch(CityAction.fetchCities()),
    ])

    const {
        isLoaded: isFiltersLoaded,
        progress: progressFilter,
    } = useProgressDispatch(() => [
        dispatch(StationAction.fetchFilters()),
        dispatch(PiezometryAction.fetchPiezometersReferents()),
        dispatch(NetworkAction.fetchNetworks()),
    ])

    const {
        isLoaded: isCampaignsLoaded,
        progress: progressCampaigns,
    } = useProgressDispatch(() => {
        if (isLoaded) {
            return [
                dispatch(CampaignAction.fetchPiezometryCampaigns()),
                dispatch(CampaignAction.fetchPiezometryCampaignStations()),
            ]
        }
        return []
    }, [isLoaded])

    useEffect(() => {
        if (!keys(piezosIndex).length) {
            dispatch(PiezometryAction.fetchPiezometersLight())
        }
    }, [])

    useTitle(() => [{
        title: i18n.piezometry,
        href: 'piezometry',
    }, {
        title: i18n.dashboard,
        href: 'piezometry',
    }], [])

    const {
        value: isAddStationPopupOpen,
        setTrue: openAddStationPopup,
        setValue: setAddStationPopupOpen,
    } = useBoolean(false)

    const {
        value: isExportOpen,
        setTrue: onOpenExport,
        setFalse: onCloseExport,
    } = useBoolean(false)

    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const [filter, setFilter] = useState(() => {
        const {
            filter: filterCode = -1,
            filterResults = [],
            searchValue = '',
        } = selectedSearchValues.piezometry || {}
        return {
            filter: filterCode,
            filterResults,
            searchValue: globalResearch || searchValue,
        }
    })

    const contributorLinks = [
        ...uniqBy(piezometersReferents, 'code'),
        ...uniqBy(piezometersOperators, 'code'),
    ]

    const networksHabilitation = accountHabilitations.filter(({ habilitation }) => habilitation.match('^PIEZO_NETWORK_*')).map(({ habilitation }) => parseInt(habilitation.substr(14)))
    const contributorsHabilitation = accountHabilitations.filter(({ habilitation }) => habilitation.match('^PIEZO_CONTRIB_*')).map(({ habilitation }) => parseInt(habilitation.substr(14)))
    const networksContributors = networks.filter(({ contributorCode }) => contributorsHabilitation.includes(contributorCode))
    const networksId = [...networkPiezoLink.map(({ idNetwork }) => idNetwork), ...networksHabilitation]
    const networklistFound = uniq(networksId).map(id => networks.find(n => n.id === id)).filter(n => !!n)
    const piezoNetworks = uniqBy([...networksContributors, ...networklistFound], 'id')

    const piezoNatures = useSandreList(SANDRE.PIEZOMETER_NATURE)
    const now = moment().valueOf()

    const filterFunc = (data) => {
        const {
            searchValue,
            filter: filterCode = -1,
            filterResults = [],
            network,
            referentIds = [],
            operatorIds = [],
        } = filter
        const ids = filterResults.map(p => p.id)
        const search = normalizeString(searchValue)
        const filterSearch = search ? data.filter(p => p.searchValue.includes(search)) : data
        const filterStation = filterCode !== -1 ? filterSearch.filter(p => ids.includes(p.id)) : filterSearch
        const filterNetwork = hasValue(network) ? filterStation.filter(p => networkPiezoLink.filter(n => n.idPiezometer === p.id).some(n => n.idNetwork === network)) : filterStation
        const filterReferent = referentIds.length === 0 ?
            filterNetwork :
            filterNetwork.filter(s => contributorLinks.filter(l => l.contributorType === referentType && referentIds.includes(l.idContributor) && (l.endDate >= now || !l.endDate)).some(l => l.idStation === s.id))
        const filterOperator = operatorIds.length === 0 ?
            filterReferent :
            filterReferent.filter(s => contributorLinks.filter(l => l.contributorType === operatorType && operatorIds.includes(l.idContributor) && (l.endDate >= now || !l.endDate)).some(l => l.idStation === s.id))
        return filterOperator
    }

    useActions(() => {
        const exportAction = {
            exportList: [{
                onClick: onOpenExport,
                label: i18n.excel,
            }],
        }
        if (accountUser.isAdmin === '1' || accountUser.metadata === '1') {
            return {
                ...exportAction,
                new: openAddStationPopup,
            }
        }
        return exportAction
    }, [accountUser])

    const { piezometry: searchValues } = selectedSearchValues
    const initialSort = searchValues?.sort

    const onTableSort = (piezometers, sort) => {
        dispatch(AdministrationAction.setSelectedSearchValues(STATION_TYPE_NAME.piezometry, { stations: sortRows(piezometers, sort), sort }))
    }

    const onSaveSearchValue = piezometers => {
        dispatch(AdministrationAction.setSelectedSearchValues(STATION_TYPE_NAME.piezometry, { stations: sortRows(piezometers, initialSort), initialSort }))
    }

    const campaignsFiltered = useMemo(() => piezometryCampaigns.filter(c => {
        const {
            nbStation = 0,
            nbStationValidated = 0,
        } = piezometryCampaignsProgress.find(cp => cp.id === c.id) || {}
        return nbStation !== 0 && nbStationValidated < nbStation && c.statut === 2
    }), [piezometryCampaigns, piezometryCampaignsProgress])

    const piezometersFormated = useMemo(() => {
        return piezometerListSpecific.map(piezo => {
            const referentLinks = piezometersReferents.filter(link => link.idStation === piezo.id && (link.endDate >= now || !link.endDate)) || []
            const referentNames = uniq(compact(referentLinks).map(l => l.name)).join(', ')
            const operatorLink = piezometersOperators.find(link => link.idStation === piezo.id && (link.endDate >= now || !link.endDate)) || {}
            const city = citiesIndex[piezo.townCode]

            const campaignStationsFiltered = piezometryCampaignStations.filter(cs => campaignsFiltered.some(c => c.id === cs.campaignId))

            const campaignStations = campaignStationsFiltered.filter(s => s.stationId === piezo.id)
            const campaigns = campaignStations.map(s => campaignsFiltered.find(c => c.id === s.campaignId))

            const creationDate = getDate(piezo.creationDate)

            const departmentCode = piezo.townCode ? getDeptCode(piezo.townCode) : ''

            const department = piezo.townCode && city && (!city.countryCode || city.countryCode === 'FR')
                ? `${getLabel(departments, departmentCode)} [${departmentCode}]` : ''

            const filterValuesFound = filterResultsProps.find(fd => fd.id === piezo.id) || {}
            const filterValues = mapValues(omit(filterValuesFound, ['id']), value => ({ value: value !== 'null' ? value : '' }))

            return {
                ...piezo,
                id: piezo.id,
                code: piezo.code,
                bookmark: { value: getBookmarks(piezo.code, getUserBookmarksByStationType(userBookmarks, STATION_TYPE_NAME.piezometry)) },
                jobExeId: { value: hasValue(piezo.jobExecutionId) && <Icon icon='wifi' style={{ fontSize: '1.5rem' }} /> },
                labelCode: { value: getStationDesignation(piezo) },
                internalReferences: { value: piezo.internalReferences },
                department: { value: department },
                cityCode: { value: piezo.townCode },
                cityLabel: { value: city?.name },
                name: { value: piezo.name || '' },
                creationDate: { value: creationDate, sortValue: piezo.creationDate },
                closeDate: { value: getDate(piezo.closeDate), sortValue: piezo.closeDate },
                piezometerNature: { value: getLabel(piezoNatures, piezo.nature) },
                administrator: { value: referentNames },
                campaigns: { value: !!campaigns.length && <Icon icon='nature_people' tooltip={campaigns.map(c => c.name).join('</br>')} size='tiny' /> },
                colorLine: piezo.closeDate ? 'grey' : 'white',
                referentId: referentLinks[0]?.idContributor,
                operatorId: operatorLink.idContributor,
                searchValue: normalizeString(`${piezo.code}${piezo.internalReferences}${piezo.name}${city?.name || ''}${piezo.townCode}${creationDate}${referentNames}${department}`.replace('undefined', '').replace('null', '')),
                ...filterValues,
            }
        })
    }, [piezometerListSpecific, piezometersReferents, piezometersOperators, citiesIndex, piezometryCampaignStations, departments, filterResultsProps, userBookmarks, piezoNatures, campaignsFiltered])

    const piezometersFiltered = useMemo(() => filterFunc(piezometersFormated), [filter, networkPiezoLink, piezometersFormated])

    const piezoMap = useMemo(() => {
        const searchData = piezometerListSpecific.map(piezo => {
            const referentLink = piezometersReferents.find(link => link.idStation === piezo.id) || {}
            const city = citiesIndex[piezo.townCode]
            const creationDate = getDate(piezo.creationDate)

            const department = piezo.townCode && city && (!city.countryCode || city.countryCode === 'FR') ? `${getLabel(departments, piezo.townCode.substr(0, 2))} [${piezo.townCode.substr(0, 2)}]` : ''
            return {
                ...piezo,
                searchValue: normalizeString(`${piezo.code}${piezo.internalReferences}${piezo.name}${city?.name || ''}${piezo.townCode}${creationDate}${referentLink.name}${department}`.replace('undefined', '').replace('null', '')),
            }
        })
        return filterFunc(searchData)
    }, [filter, networkPiezoLink, piezometerListSpecific])

    const {
        isLoaded: isStatsLoaded,
    } = useProgressDispatch(() => isLoaded ? [dispatch(PiezometryAction.fetchKeyFigures())] : [], [isLoaded])

    return (
        <>
            <Grid container spacing={1}>
                <Grid size={9}>
                    {isLoaded && isCampaignsLoaded ? <CampaignPanel /> : <ProgressCard progress={!isLoaded ? 0 : progressCampaigns} withMessage />}
                </Grid>
                <Grid size={3}>
                    <SimpleKeyFigurePanel
                        data={[
                            ...piezometryKeyfigures,
                            { title: 'stations', value: piezometerListSpecific.length },
                        ]}
                        loading={!isStatsLoaded}
                    />
                </Grid>
                <Grid size={12}>
                    {isLoaded ? (
                        <SimpleTabList
                            defaultTab={LIST_STATION}
                            tabs={[
                                {
                                    constant: LIST_STATION,
                                    label: i18n.table,
                                    icon: 'list',
                                },
                                {
                                    constant: CARTO_STATION,
                                    label: i18n.map,
                                    icon: 'map',
                                },
                                {
                                    constant: REFERENT,
                                    label: i18n.administrator,
                                    icon: 'person',
                                },
                            ]}
                            headerComponent={isFiltersLoaded ? (
                                <StationFilterFields
                                    defaultFilter={filter}
                                    onValidate={setFilter}
                                    stationType={STATION_TYPE_NAME.piezometry}
                                    contributorLinks={contributorLinks}
                                    stations={piezometersFormated}
                                    networklist={piezoNetworks}
                                />
                            ) : <ProgressCard progress={progressFilter} withMessage />}
                            noPadding
                        >
                            {
                                tab => (
                                    <>
                                        {tab === LIST_STATION && (
                                            <TablePanel
                                                piezometers={piezometersFiltered}
                                                initialSort={initialSort}
                                                onTableSort={onTableSort}
                                                onSaveSearchValue={onSaveSearchValue}
                                                filter={filter}
                                            />
                                        )}
                                        {tab === CARTO_STATION && <CartoPanel piezometersFiltered={piezoMap} />}
                                        {tab === REFERENT && <ReferentPanel piezometers={piezometersFiltered} />}
                                    </>
                                )
                            }
                        </SimpleTabList>
                    ) : <ProgressCard progress={progress} withMessage />}
                </Grid>
            </Grid>
            <AddPiezoStepper
                isOpen={isAddStationPopupOpen}
                setIsOpen={setAddStationPopupOpen}
            />
            <ExportModal
                isOpen={isExportOpen}
                onClose={onCloseExport}
                filteredPiezometers={piezometersFiltered}
            />
        </>
    )
}

export default PiezometersDashboardApp
